/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable } from 'react-native';
import {
  CurrencyItem,
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedCurrencies,
  SupportedLocales,
  useAppConfig,
  useCurrency,
  useDeviceInfo,
  useLocale,
} from '@airasia-rn/ui';
import { CURRENCY_LIST, SUPPORTED_CURRENCIES } from '@airasia-rn/ui/config';
import { useUnistyles } from 'react-native-unistyles';
import { styles } from './styles';
import { useMoveWebPageAnalytics } from './useMoveWebPageAnalytics';
import { useAuthWidget, useLayoutData } from '../../hooks';
import {
  accountKeys,
  useIsLoggedIn,
  useUser,
} from '@airasia-rn/account/hooks/account';
import { useLifeProfile } from '@airasia-rn/account/hooks/useLifeProfile';
import { useIsAllStar } from '@airasia-rn/account/hooks/useIsAllStar';
import { useAnalyticsUserProperties } from '../../hooks/useAnalyticsUserProperties';
import { KEY_IS_ALL_STAR } from '../../config/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { LocaleCurrencyView } from '@airasia-rn/account/widgets/LocaleCurrencyView';
import Page from '@airasia-phoenix/react-native/pages/PageView';
import type { CenterSheetViewRef } from '@airasia-phoenix/react-native/components/CentreSheetView';
import type { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import SectionView from '@airasia-phoenix/react-native/widgets/SectionView';
import { MoveWebPageProps } from './types';
import {
  CURRENCY_COOKIE_LABEL,
  LOCAL_CURRENCY_EXPIRY,
  LOCALE_COOKIE_LABEL,
} from './constants';
import { setCookie } from '../../utils/cookies';
import { getSafeLocalStorageItem } from '../../utils/safeLocalStorage';
import { measureInViewport, useTranslationsVersion } from '../../utils';
import { LoginBottomSheet } from '@airasia-rn/account/widgets/LoginBottomSheet';
import { LoginSheet } from '@airasia-rn/account/widgets/LoginSheet';
import { WebLayoutView } from '../../pages/WebLayoutView';
import { NavigationContext } from '../../contexts/NavigationContext';
import { useTranslation } from 'react-i18next';
// Subpath: the nps barrel would pull the NPS/CSAT pages and feedback UI in.
import { useFeedback } from '@airasia-rn/nps/feedback-context';
import type { FeedbackComponentType } from '@airasia-rn/nps/types';

// Both menus are invisible until the user opens them, so they are fetched and
// rendered on first open rather than mounted during the render that follows
// hydration. React.lazy also moves them (and, via LocaleCurrencyMenu, Phoenix's
// SectionView) out of the initial chunk.
const AccountMenuStatic = React.lazy(async () => {
  const { AccountMenuStatic } = await import(
    '@airasia-rn/web/components/AccountMenuStatic'
  );
  return { default: AccountMenuStatic };
});
// Deferred to idle (see `ssoReady`): mounting this injects the external SSO
// bundle, which Lighthouse measured as the single longest task on the page —
// 340ms, starting ~2.9s in, long after LCP.
const SSOWidgetContainer = React.lazy(async () => {
  const { LoginContainer } = await import(
    '@airasia-rn/web/widgets/SSOWidgetContainer'
  );
  return { default: LoginContainer };
});

const DEFAULT_LOCALE_LIST: MemoizedListViewProps[] = Object.entries(
  SUPPORTED_LANGUAGES,
).map(([locale, label]) => ({
  id: locale,
  primaryText: `${locale} - ${label}`,
  model: { id: locale },
  title: '',
}));

export const MoveWebPageClient: React.FC<MoveWebPageProps> = ({
  height,
  header,
  footer,
  user,
  accountMenu,
  children,
  customHeader,
  mobileCustomHeader,
  showFooter = false,
  hideFooterOnMobile = false,
  feedbackScreenDetails,
  lob,
  slug,
  screenName,
  localeList: localeListFromProp,
  currencyList: currencyListFromProp,
  apiKey = '',
  enableGuestLogin,
  showLoginWidget,
  requireLogin,
  remountOnHydration,
  disableContentMaxWidth,
  paddingBottom,
  showCurrency = true,
  showLocale = true,
  bottomTabHeight,
  serverCookieValueForCookieBanner,
  hydrationSafeCookieBanner,
  initialLayoutData,
}) => {
  const { isMobileWidth } = useDeviceInfo();
  const resolvedCustomHeader =
    mobileCustomHeader && isMobileWidth ? mobileCustomHeader : customHeader;
  const resolvedShowFooter =
    showFooter && !(hideFooterOnMobile && isMobileWidth);

  // Fetch layout data on client ONLY if not provided via server-side initialLayoutData
  const shouldFetchOnClient =
    !initialLayoutData && (!header || !footer) && !resolvedCustomHeader;
  const { data: clientLayoutData } = useLayoutData(
    apiKey,
    shouldFetchOnClient,
    slug,
  );

  const layoutData = initialLayoutData ?? clientLayoutData;

  const headerData = header ?? layoutData?.header;
  const footerData = footer ?? layoutData?.footer;

  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  // The SSO widget renders nothing until the user opens login, but mounting it
  // injects a ~367kB third-party bundle. Waiting for idle keeps Google One Tap
  // working (it still prompts on its own, just after the page settles) while
  // taking its execution out of the load window. The timeout bounds the wait on
  // a busy main thread; Safari without requestIdleCallback falls back to a
  // plain timer.
  const [ssoReady, setSsoReady] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      setSsoReady(true);
      return;
    }
    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
        cancelIdleCallback?: (handle: number) => void;
      }
    ).requestIdleCallback;

    if (typeof ric === 'function') {
      const handle = ric(() => setSsoReady(true), { timeout: 3000 });
      return () => {
        (
          window as unknown as { cancelIdleCallback?: (h: number) => void }
        ).cancelIdleCallback?.(handle);
      };
    }
    const timer = setTimeout(() => setSsoReady(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const SHOW_V6_AUTH =
    typeof window !== 'undefined' &&
    Platform.OS === 'web' &&
    getSafeLocalStorageItem('showV6Auth');

  const sheetRef = useRef<CenterSheetViewRef>(null);
  const accountButtonRef = useRef<React.ComponentRef<typeof Pressable>>(null);

  const queryClient = useQueryClient();
  const { theme } = useUnistyles();
  const {
    config: { appVersion },
    updateConfig,
    toggleDarkMode,
  } = useAppConfig();
  const { currency, setCurrency } = useCurrency();
  const { changeLanguage, currentLanguage } = useLocale();

  useEffect(() => {
    setCookie(CURRENCY_COOKIE_LABEL, currency, {
      days: LOCAL_CURRENCY_EXPIRY,
    });
    setCookie(LOCALE_COOKIE_LABEL, currentLanguage ?? DEFAULT_LANGUAGE, {
      days: LOCAL_CURRENCY_EXPIRY,
    });
  }, [currency, currentLanguage]);

  const { openFeedback } = useFeedback();
  const { t } = useTranslation('commonWeb');
  const {
    isLoggedIn,
    isLoading: isLoggedInLoading,
    isFetched: isSessionResolved,
  } = useIsLoggedIn('MoveWebPage');
  const { data: currentUser } = useUser('MoveWebPage');

  // Here, not in the account menu: that is lazily mounted only when opened.
  // This renders on every page, so it lands as soon as the session resolves.
  const isAllStar = useIsAllStar('MoveWebPage');
  useAnalyticsUserProperties({ [KEY_IS_ALL_STAR]: isAllStar?.toString() });
  const { data: lifeProfile, isLoading: isLifeProfileLoading } =
    useLifeProfile('MoveWebPage');
  const u = user ?? currentUser;
  const userFirstName =
    lifeProfile?.firstName?.trim() || u?.firstName?.trim() || undefined;
  const userAvatar = lifeProfile?.avatar?.trim();
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  // Login handled inside Login views/modal components

  // LOBs that cannot be used signed out (account-web) raise the login dialog as
  // soon as the session resolves. Visibility is derived rather than pushed into
  // state on open, so dismissing it while a session is still required is a
  // no-op, and it closes by itself once the user signs in or the LOB drops the
  // flag on a route that is reachable signed out.
  const mustLogin = requireLogin === true && isSessionResolved && !isLoggedIn;
  const isLoginDialogVisible = loginModalVisible || mustLogin;

  useAuthWidget();

  const [accountMenuVisible, setAccountMenuVisible] = useState(false);
  const [accountModalLayout, setAccountModalLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [localeCurrencyMenuVisible, setLocaleCurrencyMenuVisible] =
    useState(false);
  const [localeCurrencyDefaultTab, setLocaleCurrencyDefaultTab] = useState<
    'currency' | 'language'
  >('currency');

  const [
    isGoogleOneTapSkippedOrDismissed,
    setIsGoogleOneTapSkippedOrDismissed,
  ] = useState(false);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    if (localeCurrencyMenuVisible) {
      sheet.open();
    } else {
      sheet.close();
    }

    return () => {
      try {
        sheet?.close?.();
      } catch (error) {
        console.warn('Failed to close the sheet on cleanup:', error);
      }
    };
  }, [localeCurrencyMenuVisible]);

  const onLoginSuccess = () => {
    queryClient.invalidateQueries({ queryKey: accountKeys.refreshToken });
    queryClient.invalidateQueries({ queryKey: accountKeys.accessToken });
    queryClient.invalidateQueries({ queryKey: accountKeys.userId });
    queryClient.invalidateQueries({ queryKey: accountKeys.user });
  };

  const openV6Auth = () => {
    if (typeof window === 'undefined') return;

    window.authwidget?.openAuthModal?.({
      onSuccess: onLoginSuccess,
    });
  };

  const openAccountMenu = () => {
    if (!isLoggedIn) {
      if (SHOW_V6_AUTH) {
        // The forced dialog is already up, and the V6 widget renders its own root
        // outside this tree — opening it here would stack two login dialogs.
        if (mustLogin) return;
        openV6Auth();
        return;
      }

      setLoginModalVisible(true);
      return;
    }

    measureInViewport(accountButtonRef.current, (layout) => {
      setAccountModalLayout(layout);
      setAccountMenuVisible(true);
    });
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const openLoginModal = () => {
      if (!isLoggedIn) {
        setLoginModalVisible(true);
      }
    };
    // In case of error in google one tap login V5 sso widget trigger this event.
    window.addEventListener('openLogin', openLoginModal);
    return () => window.removeEventListener('openLogin', openLoginModal);
  }, [isLoggedIn, setLoginModalVisible]);

  const openLocaleCurrencyMenu = (tab?: 'currency' | 'language') => {
    if (tab === 'language') {
      handleLanguagePress();
    } else {
      handleCurrencyPress();
    }
    setLocaleCurrencyDefaultTab(tab ?? 'currency');
    setLocaleCurrencyMenuVisible(true);
  };

  const openLocaleCurrencyMenuFromAccount = (
    tab: 'currency' | 'language' = 'currency',
  ) => {
    if (tab === 'language') {
      handleLanguagePress();
    } else {
      handleCurrencyPress();
    }
    setLocaleCurrencyDefaultTab(tab);
    setLocaleCurrencyMenuVisible(true);
  };

  let currencyList = CURRENCY_LIST;
  if (currencyListFromProp && currencyListFromProp.length > 0) {
    currencyList = currencyListFromProp;
  } else if (layoutData?.currencyList && layoutData.currencyList.length > 0) {
    currencyList = layoutData.currencyList;
  }

  let localeList = DEFAULT_LOCALE_LIST;
  if (localeListFromProp && localeListFromProp.length > 0) {
    localeList = localeListFromProp;
  } else if (layoutData?.localeList && layoutData.localeList.length > 0) {
    localeList = layoutData.localeList;
  }

  const selectedCurrency = useMemo(() => {
    return currencyList.find((c) => c.id === currency) ?? currencyList[0]!;
  }, [currency, currencyList]);

  const selectedLanguage = useMemo(() => {
    const activeLocale = (currentLanguage ??
      DEFAULT_LANGUAGE) as SupportedLocales;
    return (
      localeList.find((l) => l.id === activeLocale) ?? {
        id: activeLocale,
        primaryText: `${activeLocale} - ${
          SUPPORTED_LANGUAGES[activeLocale] ?? activeLocale
        }`,
        model: { id: activeLocale },
        title: '',
      }
    );
  }, [currentLanguage, localeList]);

  const handleCurrencySelect = (item: CurrencyItem) => {
    if (!(item.id in SUPPORTED_CURRENCIES)) {
      console.warn('Unsupported currency selected:', item.id);
      return;
    }

    const newCurrency = item.id as SupportedCurrencies;
    setCurrency(newCurrency);
    updateConfig({ currency: newCurrency });
    setCookie(CURRENCY_COOKIE_LABEL, newCurrency, {
      days: LOCAL_CURRENCY_EXPIRY,
    });
    setLocaleCurrencyMenuVisible(false);
  };

  const handleLanguageSelect = async (item: MemoizedListViewProps) => {
    if (!item.id) {
      console.warn('No locale ID found in item:', item);
      return;
    }

    if (!(item.id in SUPPORTED_LANGUAGES)) {
      console.warn('Unsupported locale selected:', item.id);
      return;
    }

    const localeId = item.id as SupportedLocales;

    try {
      updateConfig({ locale: localeId });
      await changeLanguage(localeId);
      setCookie(LOCALE_COOKIE_LABEL, localeId, {
        days: LOCAL_CURRENCY_EXPIRY,
      });
      console.log('Language changed successfully to:', localeId);
    } catch (e) {
      console.error('changeLanguage failed for', localeId, e);
    }
    setLocaleCurrencyMenuVisible(false);
  };

  const currencyTextValue = useMemo(() => {
    return selectedCurrency?.id ?? currency ?? DEFAULT_CURRENCY;
  }, [selectedCurrency?.id, currency]);

  const languageTextValue = useMemo(() => {
    return currentLanguage
      ? String(currentLanguage).split('-')[0].toUpperCase()
      : DEFAULT_LANGUAGE.split('-')[0].toUpperCase();
  }, [currentLanguage]);

  const resolvedScreenName = screenName ?? lob ?? '';
  const eventCategory = `${lob ?? 'null'}|uhf`;

  const handleFeedbackPress = () => {
    if (!feedbackScreenDetails) return;

    const feedbackComponentType: FeedbackComponentType = isMobileWidth
      ? 'bottomSheet'
      : 'centerSheet';

    const feedbackPayload = {
      componentType: feedbackComponentType,
      screenDetails: feedbackScreenDetails,
    };

    openFeedback({
      ...feedbackPayload,
    });
  };

  const onGoogleOneTapPromptSkippedOrDismissed = () => {
    setIsGoogleOneTapSkippedOrDismissed(true);
  };

  const {
    handleAccountPress,
    handleAccountMenuItemPress,
    handleAccountMenuLogout,
    handleAccountMenuMemberIdCopy,
    handleDarkModeToggle,
    handleFooterLinkPress,
    handleCurrencyPress,
    handleLanguagePress,
    handleLogoPress,
    handleNavigationItemPress,
    handleLoginTooltipView,
    handleLoginTooltipClose,
    handleLoginTooltipLogin,
  } = useMoveWebPageAnalytics({
    isDarkMode: theme.isDarkMode,
    currencyText: currencyTextValue,
    languageText: languageTextValue,
    lob,
    screenName,
    toggleDarkMode,
  });

  useTranslationsVersion();

  return (
    <Page
      lob={lob ?? ''}
      name={resolvedScreenName}
      appVersionName={appVersion ?? ''}
    >
      <NavigationContext.Provider value={{ openAccountMenu }}>
        <WebLayoutView
          height={height}
          header={{
            ...headerData,
            navigationItems: [...(headerData?.navigationItems ?? [])],
            isLoggedIn,
            currencyText: showCurrency ? currencyTextValue : undefined,
            languageText: showLocale ? languageTextValue : undefined,
            accountText: isLoggedInLoading
              ? '...'
              : isLoggedIn
                ? (userFirstName ?? t('accountMenu.fallbackUser'))
                : t('webHeaderView.loginSignup'),
            accountAvatarText: userFirstName || 'U',
            accountAvatarUrl: isLoggedIn ? userAvatar : undefined,
            isAccountAvatarLoading: isLoggedIn && isLifeProfileLoading,
            isUserDetailLoading: isLoggedInLoading || (isLoggedIn && !u),
            onAccountPress: () => {
              handleAccountPress();
              openAccountMenu();
            },
            accountButtonRef: accountButtonRef,
            onCurrencyPress: () => openLocaleCurrencyMenu('currency'),
            onLanguagePress: () => openLocaleCurrencyMenu('language'),
            onDarkModeToggle: handleDarkModeToggle,
            onLogoPress: handleLogoPress,
            onNavigationItemPress: handleNavigationItemPress,
            cookieDisclaimer: layoutData?.cookieDisclaimer,
            serverCookieValueForCookieBanner,
            hydrationSafeCookieBanner,
          }}
          footer={
            footerData
              ? {
                  ...footerData,
                  onLinkPress: handleFooterLinkPress,
                }
              : undefined
          }
          customHeader={resolvedCustomHeader}
          showFooter={resolvedShowFooter}
          loginTooltip={{
            data: layoutData?.loginToolTip,
            isLoggedIn,
            isGoogleOneTapSkippedOrDismissed,
            bottomTabHeight,
            onView: handleLoginTooltipView,
            onClose: handleLoginTooltipClose,
            onLogin: () => {
              handleLoginTooltipLogin();
              openAccountMenu();
            },
          }}
          showFeedbackButton={Boolean(feedbackScreenDetails)}
          onFeedbackPress={handleFeedbackPress}
          feedbackLabel={t('feedbackWidget.feedback')}
          remountOnHydration={remountOnHydration}
          disableContentMaxWidth={disableContentMaxWidth}
          paddingBottom={paddingBottom}
        >
          {(!resolvedCustomHeader || showLoginWidget) &&
            hydrated &&
            // `isLoginDialogVisible` short-circuits the idle wait: if the
            // user clicks login first, or the LOB requires a session, the
            // widget mounts immediately.
            (ssoReady || isLoginDialogVisible) &&
            (Platform.OS === 'web' && !SHOW_V6_AUTH ? (
              <React.Suspense fallback={null}>
                <SSOWidgetContainer
                  isVisible={isLoginDialogVisible}
                  onClose={() => setLoginModalVisible(false)}
                  enableGuestLogin={enableGuestLogin}
                  onGoogleOneTapPromptSkippedOrDismissed={
                    onGoogleOneTapPromptSkippedOrDismissed
                  }
                />
              </React.Suspense>
            ) : isMobileWidth ? (
              <LoginBottomSheet
                visible={isLoginDialogVisible}
                onClose={() => setLoginModalVisible(false)}
              />
            ) : (
              <LoginSheet
                visible={isLoginDialogVisible}
                onClose={() => setLoginModalVisible(false)}
              />
            ))}

          {/* TODO: Temporary replacement - to be replaced by account team once ready */}
          {hydrated && (
            <React.Suspense fallback={null}>
              <AccountMenuStatic
                visible={accountMenuVisible}
                onClose={() => setAccountMenuVisible(false)}
                onOpenLocaleCurrency={openLocaleCurrencyMenuFromAccount}
                onMenuItemPress={(item) => handleAccountMenuItemPress(item.id)}
                onCopyMemberIdPress={handleAccountMenuMemberIdCopy}
                onLogoutPress={handleAccountMenuLogout}
                selectedCurrency={selectedCurrency}
                selectedLanguage={selectedLanguage}
                showCurrency={showCurrency}
                showLocale={showLocale}
                userName={userFirstName}
                memberId={u?.loyaltyId}
                avatarUrl={userAvatar}
                // Array, not a spread: a unistyles v3 style object has no enumerable
                // keys, so `...styles.container` silently dropped `position: absolute`
                // and the menu fell back to the overlay's centred layout.
                positionStyle={[
                  styles.container,
                  {
                    top: isMobileWidth
                      ? undefined
                      : (accountModalLayout?.y ?? 0) +
                        (accountModalLayout?.height ?? 0) +
                        4,
                    left: isMobileWidth
                      ? undefined
                      : (accountModalLayout?.x ?? 0) - 274,
                  },
                ]}
              />
            </React.Suspense>
          )}
          {/* {hydrated && (
            <AccountMenuModal
              {...accountMenu}
              visible={accountMenuVisible}
              userName={u?.username ?? ''}
              memberId={u?.bigMemberId ?? ''}
              items={defaultAccountMenu}
              positionStyle={{
                ...styles.container,
                top: isMobileWidth
                  ? null
                  : (accountModalLayout?.y ?? 0) +
                    (accountModalLayout?.height ?? 0) +
                    24,
                left: isMobileWidth ? null : (accountModalLayout?.x ?? 0) - 274,
              }}
              onClose={() => {
                setAccountMenuVisible(false);
              }}
            />
          )} */}

          {hydrated && (
            <SectionView category={eventCategory}>
              <LocaleCurrencyView
                visible={localeCurrencyMenuVisible}
                onClose={() => setLocaleCurrencyMenuVisible(false)}
                defaultTab={localeCurrencyDefaultTab}
                currencies={currencyList}
                selectedCurrency={selectedCurrency}
                onCurrencySelect={handleCurrencySelect}
                locale={localeList}
                selectedLanguage={selectedLanguage}
                onLanguageSelect={handleLanguageSelect}
                showCurrency={showCurrency}
                showLocale={showLocale}
              />
            </SectionView>
          )}
          {children}
        </WebLayoutView>
      </NavigationContext.Provider>
    </Page>
  );
};
